/**
 * Nav active-link sync — keeps `aria-current="page"` on the right nav link
 * across Swup navigations and animates the transition.
 *
 * The nav elements sit outside Swup's container list, so their DOM persists
 * across navigations. CSS transitions on the underline pseudo-element then
 * animate naturally as `aria-current` toggles. `.nav-link--becoming-current`
 * is added briefly before the attribute flips so the incoming wipe reads as
 * left-to-right — the default transform-origin is right (for the wipe-out
 * direction); this class overrides it to left while the new underline grows.
 */

const NAV_SELECTORS = ['#main-nav', '#mobile-main-nav'];
const BECOMING_CLASS = 'nav-link--becoming-current';
// Matches the scaleX transition duration (0.15s) with a small buffer.
const TRANSITION_MS = 200;

function getNavLinks() {
    return NAV_SELECTORS.flatMap((sel) => {
        const nav = document.querySelector(sel);
        return nav ? Array.from(nav.querySelectorAll('a.nav-link')) : [];
    });
}

function matchesPath(link, pathname) {
    try {
        return new URL(link.href).pathname === pathname;
    } catch {
        return false;
    }
}

function clearActive() {
    for (const link of getNavLinks()) {
        if (link.getAttribute('aria-current') === 'page') {
            link.removeAttribute('aria-current');
        }
    }
}

function setActiveForCurrentUrl() {
    const pathname = location.pathname;
    for (const link of getNavLinks()) {
        if (!matchesPath(link, pathname)) continue;
        if (link.getAttribute('aria-current') === 'page') continue;
        link.classList.add(BECOMING_CLASS);
        // Force reflow so the origin override takes effect before the
        // transform change kicks off the transition.
        void link.offsetWidth;
        link.setAttribute('aria-current', 'page');
        setTimeout(() => link.classList.remove(BECOMING_CLASS), TRANSITION_MS);
    }
}

export function initNavActiveSync(swup) {
    swup.hooks.on('visit:start', clearActive);
    swup.hooks.on('page:view', setActiveForCurrentUrl);
    // If the user cancels a navigation mid-transition, restore the underline
    // on the link whose URL still matches the current location.
    swup.hooks.on('visit:abort', setActiveForCurrentUrl);
}
